import type {ReactElement} from 'react';
import {useContext, useState} from 'react';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItem3} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {EventDropdown} from '@Components/event-wizard/event-dropdown';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {useEventWizardTicketingConfig} from '@Components/event-wizard/event-wizard.hooks';
import {onEventWizardTicketingWhoRespondsChange} from '@Components/event-wizard/event-wizard-slice';
import type {QuestionBelongsTo} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {getTicketingWhoRespondsOptions} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {triggerPanelClose} from '@Components/panel/panel-reducer';
import {useAppDispatch} from '@/hooks';
import {TICKETING_WHO_RESPONDS_LIST_ID, TICKETING_WHO_RESPONDS_PANEL_ID} from './ticketing-who-responds-dropdown.types';
import styles from './ticketing-who-responds-dropdown.module.scss';

export function TicketingWhoRespondsDropdown(): ReactElement {
  const dispatch = useAppDispatch();
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);
  const config = useEventWizardTicketingConfig();

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const options = getTicketingWhoRespondsOptions(config.isRsvp);
  const selectedOption = options.find((option) => option.value === config.whoResponds) ?? options[0];
  const whoRespondsLabel = window.i18next.t('pmwjs_event_ticketing_who_responds');

  const onSelectOption = (value: QuestionBelongsTo): void => {
    dispatch(onEventWizardTicketingWhoRespondsChange(value));
    setIsDropdownOpen(false);

    if (isSmallScreen) {
      dispatch(triggerPanelClose(TICKETING_WHO_RESPONDS_PANEL_ID));
    }
  };

  const getListItems = (): ControlledListItem3[] => {
    return options.map((option) => {
      return {
        id: option.value,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: option.label,
        subText: option.subText,
        isSelected: option.value === config.whoResponds,
        onClick: (): void => {
          onSelectOption(option.value);
        },
      };
    });
  };

  const getSelector = (): ReactElement => {
    return <DropdownDefaultSelectBox className="spacing-m-t-1 _full-width corner-radius-8" size={DROPDOWN_SIZE.SMALL} text={selectedOption.label} subText={selectedOption.subText} />;
  };

  return (
    <EventDropdown
      text={whoRespondsLabel}
      className="_full-width"
      selector={getSelector()}
      popup={<ControlledList className="spacing-m-0" id={TICKETING_WHO_RESPONDS_LIST_ID} items={getListItems()} />}
      popupClass={`${styles.popup} _full-width`}
      alignment={ALIGNMENT_TYPE.BOTTOM_START}
      open={isDropdownOpen}
      panelIdForModal={TICKETING_WHO_RESPONDS_PANEL_ID}
      showModalForSmallScreen={isSmallScreen}
      modalTitle={whoRespondsLabel}
      doCloseOnInnerClick
      onOpen={(): void => {
        setIsDropdownOpen(true);
      }}
      onClose={(): void => {
        setIsDropdownOpen(false);
      }}
    />
  );
}
